import React, { useEffect, useState } from "react";
import Layout from "../components/Common/Layout";
import Tables from "../components/Tables/Tables";
import { FaRegEdit, FaTrashAlt, FaDownload, FaUpload, FaSort, FaSortUp, FaSortDown, FaCalendarAlt, FaUserPlus, FaEdit } from "react-icons/fa";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { potentialLeads, getAllPotentialLeads, rejectPotentialLead, createBulkPotentialLeads, getRegionsMinimal } from "../components/Api/api";
import AdminViewToggle from "../components/Common/AdminViewToggle";
import { useAuth } from "../components/Auth/AuthContext";
import { useRegions } from '../context/RegionContext';
import { hasPermission } from "../utils/permissionUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactPaginate from "react-paginate";
import moment from 'moment';
import { erSourcesListConstant } from "../utils/constants";

const PotentialLeadsPage = () => {
  const navigate = useNavigate();
  const { user, permissions, refreshTrigger, selectedRegion, updateSelectedRegion } = useAuth();
  const { regions: contextRegions } = useRegions();
  const tableHeaders = ["Created At", "ID", "Name", "Phone", "Source", "Service Id", "Referrer Name", "Status", "Comment"];
  const [tableDataToShow, setTableDataToShow] = useState([]);
  const [showAllView, setShowAllView] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedLeadDate, setSelectedLeadDate] = useState(new Date());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tableLoading, setTableLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedComment, setSelectedComment] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [visiblePhoneId, setVisiblePhoneId] = useState(null);
  const [allRegions, setAllRegions] = useState([]);
  const [bulkUploadSelectedRegion, setBulkUploadSelectedRegion] = useState("");

  // Filter regions based on navbar selection
  const regions = contextRegions.filter(region => region.id.toString() === selectedRegion);

  const sourcesList = erSourcesListConstant;

  const statusOptions = [
    "INTERESTED",
    "INTERESTED_FOR_LATER",
    "FAILED_CONNECTION",
    "NOT_INTERESTED",
    "REJECTED"
    
  ];

  const statusesRequiringComment = [
    "REJECTED",
    "NOT_INTERESTED",
    "FAILED_CONNECTION",
    "INTERESTED_FOR_LATER"
  ];

  // Handle Create click - formDetails: [id, name, phone, service_id?]
  const handleCreateClick = (id, formDetails) => {
    const params = new URLSearchParams({
      id: formDetails[0],
      name: formDetails[1],
      phone: formDetails[2],
      source: "Potential Lead"
    });
    const serviceId = formDetails[3];
    if (serviceId != null && serviceId !== "") {
      params.set("service_id", serviceId);
    }
    navigate(`/lead-form?${params.toString()}`);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD-MM-YYYY');
  };

  const getPotentialLead = async (limit, offset, status = null) => {
    try {
      setTableLoading(true);
      const fetchFunction = showAllView ? getAllPotentialLeads : potentialLeads;
      const responseData = await fetchFunction(
        limit, 
        offset, 
        status,
        fromDate?.toISOString(),
        toDate?.toISOString()
      );
      let formatedData = [];
      responseData.items.forEach((item) => {
        const formattedDate = formatDate(item.created_at);
        const formattedSource = item.source === "REFERRAL" 
          ? `${item.source_user_type === "RA" ? "Agent_Referral" : "Runner_Referral"}`
          : item.source;
        
        // Combine comment and status_remarks if both exist
        const comment = item.status_remarks ? item.status_remarks : "-" ;
        
        formatedData.push([
          formattedDate,
          item.id,
          item.name,
          item.phone,
          formattedSource,
          item.service_id ?? null,
          item.source === "REFERRAL" ? item.source_user_name : "-",
          item.status || "-",
          comment,
        ]);
      });
      
      setTableDataToShow(formatedData);
      setTotalItems(responseData.total || 0);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch Potential Leads");
    } finally {
      setTableLoading(false);
    }
  };

  // Handle date filter
  const handleDateFilter = async () => {
    setCurrentPage(0);
    await getPotentialLead(itemsPerPage, 0, "CREATED");
  };

  // Handle page change
  const handlePageClick = async (event) => {
    const newPage = event.selected;
    setCurrentPage(newPage);
    const offset = newPage * itemsPerPage;
    await getPotentialLead(itemsPerPage, offset, "CREATED");
  };

  // Add sorting function
  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle date sorting for Created At column
      if (sortConfig.key === 0) { // Created At column
        const parseDate = (dateStr) => {
          try {
            // Parse date in DD-MM-YYYY format
            const [day, month, year] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          } catch (error) {
            console.error('Error parsing date:', dateStr, error);
            return new Date(0); // Return earliest possible date for invalid dates
          }
        };

        const dateA = parseDate(aValue);
        const dateB = parseDate(bValue);

        return sortConfig.direction === 'asc' 
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      // Handle numeric sorting for ID column
      if (sortConfig.key === 1) { // ID column
        return sortConfig.direction === 'asc' 
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      // Handle string sorting for other columns
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleSort = (columnIndex) => {
    let direction = 'asc';
    if (sortConfig.key === columnIndex && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnIndex, direction });
  };

  // Handle Close click
  const handleCloseClick = (rowIndex, formDetails) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to delete this Potential Lead!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      input: "textarea",
      inputPlaceholder: "Enter reason for closing the lead...",
      inputAttributes: {
        "aria-label": "Enter reason for closing the lead",
      },
      showLoaderOnConfirm: true,
      preConfirm: (reason) => {
        if (!reason || reason.trim() === "") {
          Swal.showValidationMessage("You need to provide a reason!");
          return false;
        }
        return reason;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const reason = result.value;
          await rejectPotentialLead(formDetails[0], reason);
          toast.success("Potential Lead closed successfully");
          getPotentialLead(100, 0, "CREATED");
        } catch (error) {
          toast.error("Failed to close Potential Lead");
        }
      } else {
        toast.info("Action canceled.");
      }
    });
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/csv") {
      setSelectedFile(file);
    } else {
      toast.error("Please upload a valid CSV file");
      event.target.value = null;
    }
  };

  // Download sample CSV
  const handleDownloadSample = () => {
    const sampleData = "name,phone,service_id\nJohn Doe,1234567890,1\nJane Smith,0987654321,2";
    const blob = new Blob([sampleData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_potential_leads.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // useEffect to set initial value for bulkUploadSelectedRegion when regions are loaded
  useEffect(() => {
    if (allRegions.length > 0 && !bulkUploadSelectedRegion) {
      setBulkUploadSelectedRegion(allRegions[0]?.id?.toString() || "");
    }
  }, [allRegions, bulkUploadSelectedRegion]);

  // Create bulk leads
  const handleCreateBulkLeads = async () => {
    if (!selectedFile) {
      toast.error("Please upload a CSV file first");
      return;
    }

    if (!selectedSource) {
      toast.error("Please select a source");
      return;
    }

    if (!selectedLeadDate) {
      toast.error("Please select a lead date");
      return;
    }

    if (!bulkUploadSelectedRegion) {
      toast.error("Please select a region");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split("\n");
        
        // Debug the raw CSV content
        console.log("Raw CSV content:", text);
        console.log("Rows:", rows);

        // Clean and validate headers
        const headers = rows[0].toLowerCase().trim().split(",").map(h => h.trim());
        console.log("Headers found:", headers);

        // Validate headers
        const requiredHeaders = ["name", "phone", "service_id"];
        const hasAllHeaders = requiredHeaders.every(header => headers.includes(header));
        console.log("Required headers check:", { headers, hasAllHeaders });
        
        if (!hasAllHeaders) {
          toast.error(`CSV file must contain name, phone and service_id columns. Found: ${headers.join(", ")}`);
          return;
        }

        // Parse CSV data with better error handling
        const leads = rows.slice(1)
          .filter(row => row.trim()) // Remove empty rows
          .map((row, index) => {
            const values = row.split(",").map(val => val.trim());
            console.log(`Row ${index + 2}:`, values); // +2 because we skip header and index starts at 0

            // Get the column indexes for each required field
            const nameIndex = headers.indexOf("name");
            const phoneIndex = headers.indexOf("phone");
            const serviceIdIndex = headers.indexOf("service_id");
            
            const lead = {
              name: values[nameIndex],
              phone: values[phoneIndex],
              service_id: parseInt(values[serviceIdIndex]),
              source: selectedSource,
              region_id: parseInt(bulkUploadSelectedRegion),
              lead_date: moment(selectedLeadDate).format('YYYY-MM-DD')
            };

            // Validate each field
            if (!lead.name || !lead.phone) {
              throw new Error(`Invalid data in row ${index + 2}: Missing required fields. Found: name=${lead.name}, phone=${lead.phone}`);
            }

            return lead;
          });

        console.log("Parsed leads:", leads);

        // Validate data
        if (leads.length === 0) {
          toast.error("No valid leads found in the CSV file");
          return;
        }

        // Send to API
        const response = await createBulkPotentialLeads(leads);
        console.log("API Response:", response);
        
        toast.success(`Successfully created ${leads.length} potential leads`);
        setSelectedFile(null);
        setSelectedSource("");
        // Reset file input
        document.getElementById("csvFileInput").value = "";
        // Refresh the leads list
        getPotentialLead(100, 0, "CREATED");
      } catch (error) {
        console.error("Error processing CSV:", error);
        toast.error(error.message || "Failed to process CSV file. Please check the format and try again.");
      }
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      toast.error("Failed to read the CSV file");
    };

    reader.readAsText(selectedFile);
  };

  // Filter table data based on search query
  const filteredData = sortData(tableDataToShow.filter(row => 
    row.slice(1, 5).some(cell => // Exclude Created At and ID, only search Name, Phone, Source
      cell && cell.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  ));

  // Add this new function to handle phone visibility toggle
  const handlePhoneVisibility = (id) => {
    setVisiblePhoneId(visiblePhoneId === id ? null : id);
  };

  // Modify the columnActions array
  const columnActions = [
    null, // Created At
    null, // ID
    null, // Name
    {
      icon: null,
      action: (rowIndex, formDetails) => (
        <div className="flex items-center justify-center">
          <button
            onClick={() => handlePhoneVisibility(formDetails[1])}
            className={`p-2 rounded-lg transition-all duration-200 hover:bg-emerald-50 ${
              visiblePhoneId === formDetails[1] ? 'text-emerald-600 bg-emerald-50' : 'text-emerald-500'
            }`}
            title={visiblePhoneId === formDetails[1] ? "Hide Phone Number" : "Show Phone Number"}
          >
            {visiblePhoneId === formDetails[1] ? (
              <div className="flex items-center gap-3">
                <span 
                  className="no-select text-gray-700 font-medium" 
                  style={{ 
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    msUserSelect: 'none',
                    MozUserSelect: 'none'
                  }}
                >
                  {formDetails[3]}
                </span>
                <MdOutlineVisibilityOff className="w-5 h-5 hover:text-emerald-700" />
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <MdOutlineVisibility className="w-5 h-5 hover:text-emerald-700" />
              </div>
            )}
          </button>
        </div>
      )
    },
    null, // Source
    null, // Referrer Name
    {
      icon: null,
      action: (rowIndex, formDetails) => (
        <div className="flex items-center gap-2">
          <select
            defaultValue={formDetails[6] || ""}
            className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            onChange={(e) => {
              const status = e.target.value;
              if (status === "INTERESTED") {
                handleCreateClick(rowIndex, [formDetails[1], formDetails[2], formDetails[3], formDetails[5]]);
              } else if (status) {
                Swal.fire({
                  title: status === "INTERESTED_FOR_LATER" ? "Select Follow-up Date" : "Add Comment",
                  html: status === "INTERESTED_FOR_LATER" ? `
                    <div class="text-center mb-4">
                      <p class="text-gray-600">You have selected</p>
                      <p class="text-red-500 font-semibold">${status.replace(/_/g, " ")}</p>
                      <div class="mt-4">
                        <input 
                          type="date" 
                          id="followupDate" 
                          class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 cursor-pointer"
                          min="${moment().format('YYYY-MM-DD')}"
                          style="position: relative; z-index: 9999;"
                          onfocus="this.showPicker()"
                        />
                      </div>
                    </div>
                  ` : `
                    <div class="text-center mb-4">
                      <p class="text-gray-600">You have selected</p>
                      <p class="text-red-500 font-semibold">${status.replace(/_/g, " ")}</p>
                    </div>
                  `,
                  input: status === "INTERESTED_FOR_LATER" ? null : "textarea",
                  inputPlaceholder: status === "INTERESTED_FOR_LATER" ? null : "Enter your comment...",
                  showCancelButton: true,
                  confirmButtonText: "Submit",
                  cancelButtonText: "Cancel",
                  showLoaderOnConfirm: true,
                  preConfirm: () => {
                    if (status === "INTERESTED_FOR_LATER") {
                      const dateInput = document.getElementById('followupDate');
                      if (!dateInput.value) {
                        Swal.showValidationMessage("Please select a follow-up date");
                        return false;
                      }
                      return `${moment(dateInput.value).format('DD-MM-YYYY')}`;
                    } else if (statusesRequiringComment.includes(status)) {
                      const comment = Swal.getInput().value;
                      if (!comment || comment.trim() === "") {
                        Swal.showValidationMessage("Comment is required for this status");
                        return false;
                      }
                      return comment;
                    }
                    return "";
                  }
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    try {
                      await rejectPotentialLead(formDetails[1], result.value, status);
                      toast.success("Lead status updated successfully");
                      getPotentialLead(itemsPerPage, currentPage * itemsPerPage, "CREATED");
                    } catch (error) {
                      toast.error("Failed to update lead status");
                    }
                  } else {
                    // Reset the select value back to empty when cancelled
                    e.target.value = "";
                  }
                });
              }
            }}
          >
            <option value="">Select Status</option>
            {statusOptions.map((status, index) => (
              <option key={index} value={status}>
                {status.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          {formDetails[6] === "INTERESTED" && (
            <button
              onClick={() => handleCreateClick(rowIndex, [formDetails[1], formDetails[2], formDetails[3], formDetails[5]])}
              className="p-2 text-green-600 hover:text-green-800 transition-colors"
              title="Create Lead"
            >
              <FaUserPlus className="w-5 h-5" />
            </button>
          )}
        </div>
      )
    },
    {
      icon: null,
      action: (rowIndex, formDetails) => (
        <div className="flex items-center gap-2">
          <span className="flex-1">{formDetails[7]}</span>
          <button
            onClick={() => {
              Swal.fire({
                title: "Edit Comment",
                input: "textarea",
                inputValue: formDetails[7],
                inputPlaceholder: "Enter your comment...",
                showCancelButton: true,
                confirmButtonText: "Save",
                cancelButtonText: "Cancel",
                showLoaderOnConfirm: true,
                preConfirm: (comment) => {
                  if (!comment || comment.trim() === "") {
                    Swal.showValidationMessage("Please enter a comment");
                    return false;
                  }
                  return comment;
                }
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await rejectPotentialLead(formDetails[1], result.value);
                    toast.success("Comment updated successfully");
                    // Update the table data immediately
                    const updatedData = [...tableDataToShow];
                    updatedData[rowIndex][7] = result.value; // Update the comment in the table data
                    setTableDataToShow(updatedData);
                    // Also refresh from server to ensure consistency
                    getPotentialLead(itemsPerPage, currentPage * itemsPerPage, "CREATED");
                  } catch (error) {
                    toast.error("Failed to update comment");
                  }
                }
              });
            }}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            title="Edit Comment"
          >
            <FaEdit className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableLoading(true);
        await getPotentialLead(itemsPerPage, currentPage * itemsPerPage, "CREATED");
      } catch (error) {
        console.error('Error fetching potential leads:', error);
        toast.error('Failed to fetch potential leads');
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [selectedRegion, refreshTrigger, showAllView, fromDate, toDate]);

  // Add debug logging
  useEffect(() => {
    console.log('User:', user);
    console.log('Permissions:', permissions);
  }, [user, permissions]);

  // Check if user has permission to upload CSV
  const canUploadCSV = hasPermission(permissions, "sourcing_agent", "bulk_upload");
  console.log('Can Upload CSV:', canUploadCSV);

  const handleStatusChange = async (rowIndex, formDetails, status) => {
    setSelectedLeadId(formDetails[0]);
    setSelectedStatus(status);
    setSelectedComment("");
  };

  const handleCommentSubmit = async () => {
    if (!selectedLeadId || !selectedStatus || (!selectedComment && selectedStatus !== "INTERESTED")) {
      toast.error("Please select a status and add a comment");
      return;
    }

    try {
      if (selectedStatus === "INTERESTED") {
        const row = tableDataToShow.find((r) => r[1] === selectedLeadId);
        if (row) {
          handleCreateClick(null, [selectedLeadId, row[2], row[3], row[5]]);
        }
      } else {
        await rejectPotentialLead(selectedLeadId, `${selectedStatus}: ${selectedComment}`);
        toast.success("Lead status updated successfully");
      }
      getPotentialLead(itemsPerPage, currentPage * itemsPerPage, "CREATED");
      setSelectedLeadId(null);
      setSelectedStatus("");
      setSelectedComment("");
    } catch (error) {
      toast.error("Failed to update lead status");
    }
  };

  const handleDownloadCompleteDump = () => {
    // Create headers without the phone column
    const headers = ["Created At", "ID", "Name", "Source", "Service Id", "Referrer Name", "Status", "Comment"];
    
    // Create CSV content
    const csvContent = [
      headers.join(','), // Header row
      ...tableDataToShow.map(row => {
        // Remove phone number (index 3) from the row
        const rowWithoutPhone = [...row];
        rowWithoutPhone.splice(3, 1); // Remove phone number
        return rowWithoutPhone.map(cell => `"${cell}"`).join(',');
      })
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `potential_leads_dump_${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Fetch all regions for bulk upload
  const fetchAllRegions = async () => {
    try {
      const regionsData = await getRegionsMinimal();
      setAllRegions(regionsData);
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Failed to fetch regions');
    }
  };

  useEffect(() => {
    if (showBulkUpload) {
      fetchAllRegions();
    }
  }, [showBulkUpload]);

  return (
    <Layout>
      <style>
        {`
          .no-select {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            -webkit-touch-callout: none;
          }
          
          .no-select::selection {
            background: transparent;
          }
        `}
      </style>
      <div className="space-y-4">
        {/* Date Filter Section */}
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-semibold mb-2">Date Filter</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={fromDate}
                onChange={date => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate || new Date()}
                placeholderText="From Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={toDate}
                onChange={date => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                maxDate={new Date()}
                placeholderText="To Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <button
              onClick={handleDateFilter}
              className="px-3 py-1 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Apply Filter
            </button>
            <button
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                handleDateFilter();
              }}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filter
            </button>
          </div>
        </div>

        {/* Top Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Search Input */}
          <div className="w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by name, phone, or source..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Admin View Toggle */}
            <AdminViewToggle 
              showAllView={showAllView} 
              onToggle={() => setShowAllView(!showAllView)} 
            />

            {/* Create Bulk Leads Button */}
            {canUploadCSV && (
              <button
                onClick={() => setShowBulkUpload(!showBulkUpload)}
                className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
              >
                <FaUserPlus />
                Create Bulk Leads
              </button>
            )}
          </div>
        </div>

        {/* Bulk Upload Section */}
        {showBulkUpload && canUploadCSV && (
          <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg shadow space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Bulk Upload Leads</h3>
              <button
                onClick={() => {
                  setShowBulkUpload(false);
                  setSelectedFile(null);
                  setSelectedSource("");
                  setSelectedLeadDate(new Date());
                  setBulkUploadSelectedRegion("");
                  document.getElementById("csvFileInput").value = "";
                }}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Source for New Leads *</label>
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                >
                  <option value="">Select Source</option>
                  {sourcesList.map((source, index) => (
                    <option key={index} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Region for New Leads *</label>
                <select
                  value={bulkUploadSelectedRegion}
                  onChange={(e) => setBulkUploadSelectedRegion(e.target.value)}
                  className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                >
                  {allRegions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Lead Date *</label>
                <DatePicker
                  selected={selectedLeadDate}
                  onChange={date => setSelectedLeadDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                onClick={handleDownloadSample}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm"
              >
                <FaDownload className="text-gray-500" />
                Download Sample CSV
              </button>
              
              <label className="flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors cursor-pointer border border-pink-100 shadow-sm">
                <FaUpload className="text-pink-500" />
                Upload CSV
                <input
                  id="csvFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>

              {selectedFile && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-600">
                  <span>Selected file:</span>
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
              )}

              <button
                onClick={handleCreateBulkLeads}
                className={`ml-auto px-5 py-2 text-white rounded-lg transition-colors shadow-sm ${
                  selectedSource && bulkUploadSelectedRegion && selectedLeadDate && selectedFile
                    ? 'bg-pink-600 hover:bg-pink-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                disabled={!selectedSource || !bulkUploadSelectedRegion || !selectedLeadDate || !selectedFile}
                title={!selectedSource || !bulkUploadSelectedRegion || !selectedLeadDate || !selectedFile
                  ? `Please select ${[
                      !selectedSource && "source",
                      !bulkUploadSelectedRegion && "region",
                      !selectedLeadDate && "lead date",
                      !selectedFile && "CSV file"
                    ].filter(Boolean).join(", ")}`
                  : ""}
              >
                Create Bulk Leads
              </button>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white rounded-lg shadow">
          {/* Table Header with Download Button */}
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Potential Leads</h2>
            {showAllView && (
              <button
                onClick={handleDownloadCompleteDump}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FaDownload />
                Download Table Data
              </button>
            )}
          </div>

          {/* Table Component */}
          <Tables
            tableHeaders={tableHeaders}
            tableDataToShow={filteredData}
            columnActions={columnActions}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>

        {/* Pagination */}
        {!tableLoading && totalItems > itemsPerPage && (
          <div className="mt-6 flex justify-center">
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={Math.ceil(totalItems / itemsPerPage)}
              previousLabel="< Previous"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center space-x-2"
              pageClassName="px-3 py-1 rounded hover:bg-pink-100"
              pageLinkClassName="text-gray-700"
              activeClassName="bg-pink-500 text-white"
              previousClassName="px-3 py-1 rounded hover:bg-pink-100"
              nextClassName="px-3 py-1 rounded hover:bg-pink-100"
              disabledClassName="opacity-50 cursor-not-allowed"
              forcePage={currentPage}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PotentialLeadsPage;
